import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, ArrowLeft, LogIn, Save, X,
  FileText, Users, Mail, Building2, Copy, RefreshCw, Target,
  Send, BookOpen, BarChart3, Sparkles, Inbox,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import OutreachTab from '../components/admin/OutreachTab';
import NewsletterTab from '../components/admin/NewsletterTab';
import KnowledgeTab from '../components/admin/KnowledgeTab';
import StatsTab from '../components/admin/StatsTab';
import PreQualTab from '../components/admin/PreQualTab';
import InboxTab from '../components/admin/InboxTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_KEY = 'sps_admin_session_v1';

const BlogAdmin = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [autoLoginChecked, setAutoLoginChecked] = useState(false);
  // Lies initial Tab aus URL-Hash (z. B. #tab=outreach), Default: articles
  const initialTab = (() => {
    const valid = ['articles', 'leads', 'outreach', 'prequal', 'inbox', 'newsletter', 'knowledge', 'stats'];
    const m = (typeof window !== 'undefined' ? window.location.hash : '').match(/tab=([^&]+)/);
    return m && valid.includes(m[1]) ? m[1] : 'articles';
  })();
  const [tab, setTabState] = useState(initialTab);

  // Hält Hash und Tab synchron, damit der Tab beim Browser-Back / per Link erhalten bleibt
  const setTab = (next) => {
    setTabState(next);
    if (typeof window !== 'undefined') {
      const newHash = `#tab=${next}`;
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${newHash}`);
      }
    }
  };

  // Articles state
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', summary: '', content: '' });

  // Leads state
  const [leads, setLeads] = useState([]);

  // Inbox unread count badge
  const [inboxCount, setInboxCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    'x-admin-password': password,
  };

  const login = async (silent = false) => {
    if (!silent) setError('');
    try {
      const res = await fetch(`${API}/admin/verify`, { method: 'POST', headers });
      if (!res.ok) throw new Error();
      setAuthenticated(true);
      try { localStorage.setItem(SESSION_KEY, password); } catch (e) { /* ignore */ }
      fetchArticles();
      fetchLeads();
      return true;
    } catch {
      if (!silent) setError('Ungültiges Passwort');
      return false;
    }
  };

  const logout = () => {
    setAuthenticated(false);
    setPassword('');
    try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
  };

  // Auto-Login: beim ersten Render einmalig localStorage-Token prüfen.
  // Wenn gültig → direkt in den (per Hash gewählten) Tab. Sonst Login zeigen.
  useEffect(() => {
    if (autoLoginChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (stored) {
          const res = await fetch(`${API}/admin/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-admin-password': stored },
          });
          if (!cancelled && res.ok) {
            setPassword(stored);
            setAuthenticated(true);
          } else if (!cancelled) {
            localStorage.removeItem(SESSION_KEY);
          }
        }
      } catch (e) {
        /* network or storage issue → fall back to manual login */
      } finally {
        if (!cancelled) setAutoLoginChecked(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch payloads after auto-login transitions to authenticated
  useEffect(() => {
    if (authenticated && password) {
      fetchArticles();
      fetchLeads();
      fetchInboxCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  // Poll inbox count every 60 sec while authenticated (cheap count endpoint).
  useEffect(() => {
    if (!authenticated || !password) return undefined;
    const id = setInterval(fetchInboxCount, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, password]);

  const fetchInboxCount = async () => {
    try {
      const res = await fetch(`${API}/admin/inbox/unmatched/count`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setInboxCount(data.count || 0);
    } catch {
      /* silent — non-critical */
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/blog`, { headers });
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const res = await fetch(`${API}/admin/whitepaper-leads`, { headers });
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (err) {
      console.error(err);
    }
  };

  const saveArticle = async () => {
    setLoading(true);
    try {
      if (editing) {
        await fetch(`${API}/admin/blog/${editing}`, {
          method: 'PUT', headers, body: JSON.stringify(form),
        });
      } else {
        await fetch(`${API}/admin/blog`, {
          method: 'POST', headers, body: JSON.stringify(form),
        });
      }
      setForm({ title: '', summary: '', content: '' });
      setEditing(null);
      fetchArticles();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Artikel wirklich löschen?')) return;
    try {
      await fetch(`${API}/admin/blog/${id}`, { method: 'DELETE', headers });
      fetchArticles();
    } catch (err) {
      console.error(err);
    }
  };

  const togglePublished = async (article) => {
    try {
      await fetch(`${API}/admin/blog/${article.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ published: !article.published }),
      });
      fetchArticles();
    } catch (err) {
      console.error(err);
    }
  };

  const startEdit = (article) => {
    setEditing(article.id);
    setForm({ title: article.title, summary: article.summary, content: article.content });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ title: '', summary: '', content: '' });
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Lead wirklich löschen?')) return;
    try {
      await fetch(`${API}/admin/whitepaper-leads/${id}`, {
        method: 'DELETE', headers,
      });
      fetchLeads();
    } catch (err) {
      console.error(err);
    }
  };

  const copyEmails = () => {
    const emails = leads.map((l) => l.email).join(', ');
    navigator.clipboard.writeText(emails);
  };

  const exportCsv = () => {
    if (!leads.length) return;
    const header = ['Name', 'Email', 'Unternehmen', 'Datum'];
    const rows = leads.map((l) => [
      l.name,
      l.email,
      l.company || '',
      new Date(l.created_at).toLocaleString('de-DE'),
    ]);
    const escape = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(escape).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whitepaper-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authenticated) {
    // Während der Auto-Login-Check läuft, kein Login-Form zeigen (sonst flackert es kurz auf)
    if (!autoLoginChecked) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
          <div className="text-sm text-slate-500" data-testid="admin-auto-check">
            Sitzung wird geprüft…
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md border border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-6 text-center">Admin-Bereich</h1>
          {error && (
            <p className="text-red-600 text-sm mb-4 text-center" data-testid="admin-error">
              {error}
            </p>
          )}
          <Input
            type="password"
            placeholder="Admin-Passwort"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login(false)}
            className="mb-4"
            data-testid="admin-password-input"
          />
          <Button
            onClick={() => login(false)}
            className="w-full bg-slate-800 hover:bg-slate-700"
            data-testid="admin-login-btn"
          >
            <LogIn size={18} className="mr-2" /> Anmelden
          </Button>
          <p className="text-[11px] text-slate-400 mt-3 text-center">
            Diese Sitzung bleibt auf diesem Gerät dauerhaft angemeldet, bis du dich abmeldest.
          </p>
          <a
            href="/"
            className="block text-center text-sm text-slate-500 hover:text-slate-700 mt-4"
          >
            Zurück zur Website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Admin-Bereich</h1>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={logout}
              className="text-sm text-slate-500 hover:text-rose-600 transition-colors"
              data-testid="admin-logout-btn"
              title="Sitzung beenden — beim nächsten Besuch wird wieder das Passwort abgefragt"
            >
              Abmelden
            </button>
            <a
              href="/"
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft size={16} /> Zur Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="max-w-5xl mx-auto flex gap-1">
          <button
            onClick={() => setTab('articles')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'articles'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-articles"
          >
            <FileText size={16} />
            Blog-Artikel ({articles.length})
          </button>
          <button
            onClick={() => setTab('leads')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'leads'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-leads"
          >
            <Users size={16} />
            Whitepaper-Leads ({leads.length})
          </button>
          <button
            onClick={() => setTab('outreach')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'outreach'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-outreach"
          >
            <Target size={16} />
            Outreach
          </button>
          <button
            onClick={() => setTab('prequal')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'prequal'
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-prequal"
          >
            <Sparkles size={16} />
            Vorqualifikation
          </button>
          <button
            onClick={() => setTab('inbox')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
              tab === 'inbox'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-inbox"
          >
            <Inbox size={16} />
            Inbox
            {inboxCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-orange-500 text-white"
                data-testid="admin-tab-inbox-badge"
              >
                {inboxCount > 99 ? '99+' : inboxCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('newsletter')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'newsletter'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-newsletter"
          >
            <Send size={16} />
            Newsletter
          </button>
          <button
            onClick={() => setTab('knowledge')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'knowledge'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-knowledge"
          >
            <BookOpen size={16} />
            Wissen
          </button>
          <button
            onClick={() => setTab('stats')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'stats'
                ? 'border-slate-800 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="admin-tab-stats"
          >
            <BarChart3 size={16} />
            Auswertung
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {tab === 'articles' && (
          <>
            {/* Editor */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 mb-8 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {editing ? 'Artikel bearbeiten' : 'Neuer Artikel'}
              </h2>
              <div className="space-y-4">
                <Input
                  placeholder="Titel"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="admin-title-input"
                />
                <Input
                  placeholder="Kurzbeschreibung (wird in der Übersicht angezeigt)"
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  data-testid="admin-summary-input"
                />
                <Textarea
                  placeholder="Artikeltext"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="min-h-[200px]"
                  data-testid="admin-content-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={saveArticle}
                    disabled={!form.title || !form.summary || !form.content || loading}
                    className="bg-slate-800 hover:bg-slate-700"
                    data-testid="admin-save-btn"
                  >
                    <Save size={16} className="mr-2" />
                    {editing ? 'Aktualisieren' : 'Veröffentlichen'}
                  </Button>
                  {editing && (
                    <Button variant="outline" onClick={cancelEdit} data-testid="admin-cancel-btn">
                      <X size={16} className="mr-2" /> Abbrechen
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Articles List */}
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Alle Artikel ({articles.length})
            </h2>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
              </div>
            ) : articles.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Noch keine Artikel vorhanden.</p>
            ) : (
              <div className="space-y-4">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm flex items-start justify-between gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 truncate">{article.title}</h3>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            article.published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {article.published ? 'Veröffentlicht' : 'Entwurf'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">{article.summary}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(article.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => togglePublished(article)}
                        className="p-2 hover:bg-slate-100 rounded-md transition-colors"
                        title={article.published ? 'Verbergen' : 'Veröffentlichen'}
                      >
                        {article.published ? (
                          <Eye size={16} className="text-green-600" />
                        ) : (
                          <EyeOff size={16} className="text-slate-400" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(article)}
                        className="p-2 hover:bg-slate-100 rounded-md transition-colors"
                        title="Bearbeiten"
                      >
                        <Edit2 size={16} className="text-slate-600" />
                      </button>
                      <button
                        onClick={() => deleteArticle(article.id)}
                        className="p-2 hover:bg-red-50 rounded-md transition-colors"
                        title="Löschen"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'leads' && (
          <>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Whitepaper-Leads ({leads.length})
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Anfragen für „5 Hebel zur Portooptimierung"
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchLeads}
                  data-testid="admin-leads-refresh"
                >
                  <RefreshCw size={14} className="mr-2" /> Aktualisieren
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyEmails}
                  disabled={!leads.length}
                  data-testid="admin-leads-copy-emails"
                >
                  <Copy size={14} className="mr-2" /> E-Mails kopieren
                </Button>
                <Button
                  size="sm"
                  onClick={exportCsv}
                  disabled={!leads.length}
                  className="bg-slate-800 hover:bg-slate-700"
                  data-testid="admin-leads-export-csv"
                >
                  CSV-Export
                </Button>
              </div>
            </div>

            {leads.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                <Users size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Noch keine Whitepaper-Anfragen.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">E-Mail</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Unternehmen</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Datum</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                        data-testid={`admin-lead-row-${lead.id}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-900">{lead.name}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <a
                            href={`mai` + `lto:${lead.email}`}
                            className="hover:underline inline-flex items-center gap-1.5"
                          >
                            <Mail size={12} className="text-slate-400" />
                            {lead.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {lead.company ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Building2 size={12} className="text-slate-400" />
                              {lead.company}
                            </span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {new Date(lead.created_at).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => deleteLead(lead.id)}
                            className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
                            title="Lead löschen"
                            data-testid={`admin-lead-delete-${lead.id}`}
                          >
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === 'outreach' && (
          <OutreachTab password={password} />
        )}

        {tab === 'prequal' && (
          <PreQualTab password={password} />
        )}

        {tab === 'inbox' && (
          <InboxTab password={password} />
        )}

        {tab === 'newsletter' && (
          <NewsletterTab password={password} />
        )}

        {tab === 'knowledge' && (
          <KnowledgeTab password={password} />
        )}

        {tab === 'stats' && (
          <StatsTab password={password} />
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
